import type { NewsItem, Project } from '@/lib/api';
import { achievementNewsTag, directionNewsTags, type DirectionId } from '@/lib/labDirectionTags';
import { sceneAsset } from './sceneAssets';

export { achievementNewsTag, directionNewsTags, type DirectionId } from '@/lib/labDirectionTags';

export const directions: Array<{
  id: DirectionId;
  title: string;
  icon: string;
  slogan: string;
  eyebrow: string;
  description: string;
  model: string;
  poster: string;
}> = [
  {
    id: 'robotics',
    title: 'Робототехника',
    icon: 'RobotArm',
    slogan: 'Точность, воплощённая в движении.',
    eyebrow: '01 / Направление',
    description: 'Манипуляторы, захваты и управление движением.',
    model: sceneAsset('robotics', 'glb'),
    poster: sceneAsset('robotics', 'png'),
  },
  {
    id: 'ai',
    title: 'Искусственный интеллект',
    icon: 'Brain',
    slogan: 'От данных — к интеллекту.',
    eyebrow: '02 / Направление',
    description: 'Обучающие платформы и голосовые интерфейсы.',
    model: sceneAsset('ai', 'glb'),
    poster: sceneAsset('ai', 'png'),
  },
  {
    id: 'electronics',
    title: 'Электроника',
    icon: 'Lightning',
    slogan: 'Даём идеям импульс.',
    eyebrow: '03 / Направление',
    description: 'Контроллеры, датчики и управление устройствами.',
    model: sceneAsset('electronics', 'glb'),
    poster: sceneAsset('electronics', 'png'),
  },
  {
    id: 'software',
    title: 'Программные системы',
    icon: 'Code',
    slogan: 'Превращаем идеи в работающие системы.',
    eyebrow: '04 / Направление',
    description: 'Платформы для обучения и робототехники.',
    model: sceneAsset('software', 'glb'),
    poster: sceneAsset('software', 'png'),
  },
];

export function getDirectionProjects(direction: DirectionId, projects: readonly Project[]): Project[] {
  const seen = new Set<number>();
  return projects.filter(item => {
    if (!item || !Number.isSafeInteger(item.id) || item.id <= 0 || seen.has(item.id)
      || item.is_public !== true
      // Публичный каталог уже проверяет владельца и модерацию, но не отдаёт moderation_status.
      || (item.moderation_status != null && item.moderation_status !== 'approved')
      || typeof item.name !== 'string' || !item.name.trim()
      || !Array.isArray(item.directions) || !item.directions.includes(direction)) return false;
    seen.add(item.id);
    return true;
  });
}

function hasSafeNewsSlug(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const slug = value.trim();
  if (!slug || slug === '.' || slug === '..' || /[\\/?#%:<>"'\s\u0000-\u001f\u007f]/.test(slug)) return false;
  try {
    encodeURIComponent(slug);
    return true;
  } catch {
    return false;
  }
}

export function getNewsPublicationTime(value: unknown): number {
  if (typeof value !== 'string') return -Infinity;
  const normalized = value.trim().replace(' ', 'T');
  if (!/^\d{4}-\d{2}-\d{2}(?:T(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d(?:\.\d{1,6})?)?(?:Z|[+-]\d{2}:\d{2})?)?$/.test(normalized)) return -Infinity;
  const day = normalized.slice(0, 10);
  const calendar = Date.parse(`${day}T00:00:00Z`);
  if (!Number.isFinite(calendar) || new Date(calendar).toISOString().slice(0, 10) !== day) return -Infinity;
  // SQL-даты без зоны сравниваем в UTC, независимо от часового пояса браузера.
  const explicitZone = /(?:Z|[+-]\d{2}:\d{2})$/.test(normalized);
  const timestamp = Date.parse(normalized.includes('T') && !explicitZone ? `${normalized}Z` : normalized);
  return Number.isFinite(timestamp) ? timestamp : -Infinity;
}

export function getDirectionNews(direction: DirectionId, projects: readonly Project[], news: readonly NewsItem[]): NewsItem[] {
  const visibleProjects = getDirectionProjects(direction, projects);
  const projectIds = new Set(visibleProjects.map(project => project.id));
  const linkedNewsIds = new Set(visibleProjects.flatMap(project =>
    Array.isArray(project.linked_news) ? project.linked_news.map(item => item.id) : []));
  return selectPublishedNews(news, item =>
    (Array.isArray(item.tags) && item.tags.some(tag => tag?.slug === directionNewsTags[direction]))
      || linkedNewsIds.has(item.id)
      || (Array.isArray(item.project_ids) && item.project_ids.some(id => projectIds.has(id)))
      || (Array.isArray(item.projects) && item.projects.some(project => projectIds.has(project?.id))));
}

export function getAchievementNews(news: readonly NewsItem[]): NewsItem[] {
  return selectPublishedNews(news, item =>
    Array.isArray(item.tags) && item.tags.some(tag => tag?.slug === achievementNewsTag));
}

function selectPublishedNews(news: readonly NewsItem[], belongs: (item: NewsItem) => boolean): NewsItem[] {
  const matches = news.filter(item => {
    if (!item || !Number.isSafeInteger(item.id) || item.id <= 0
      || item.status !== 'published' || item.is_published === false || !hasSafeNewsSlug(item.slug)) return false;

    return belongs(item);
  });
  const seen = new Set<number>();
  return matches.sort((left, right) => {
    const leftTime = getNewsPublicationTime(left.published_at);
    const rightTime = getNewsPublicationTime(right.published_at);
    if (leftTime !== rightTime) return rightTime - leftTime;
    return left.id - right.id || (left.slug < right.slug ? -1 : left.slug > right.slug ? 1 : 0);
  }).filter(item => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}
